import React, { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  ChevronRight,
  CircleUserRound,
  Clock3,
  LogIn,
  LogOut,
  MapPin,
  Minus,
  Plus,
  Search,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Store,
  Truck,
  UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  createPublicWebshopOrder,
  getCategories,
  getProducts,
  getPublicWebshop,
  getSettings,
  getTables,
  syncPublicWebshopCustomer,
} from '../lib/api';
import { firebaseAuth, googleAuthProvider } from '../lib/firebase';
import {
  buildCatalogViewFromMenu,
  buildEmptySalesCatalog,
  getMenusForScope,
  normalizeSalesCatalog,
  scopeFromFulfillment,
} from '../lib/salesCatalog';

function formatMoney(value, currency = 'EUR') {
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function weekdayKey(date = new Date()) {
  return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
}

function mergeDeep(base, override) {
  if (Array.isArray(base)) {
    return Array.isArray(override) ? override : base;
  }
  if (!base || typeof base !== 'object') {
    return override ?? base;
  }

  const next = { ...base };
  for (const key of Object.keys(override || {})) {
    const baseValue = base[key];
    const overrideValue = override[key];
    if (
      baseValue
      && typeof baseValue === 'object'
      && !Array.isArray(baseValue)
      && overrideValue
      && typeof overrideValue === 'object'
      && !Array.isArray(overrideValue)
    ) {
      next[key] = mergeDeep(baseValue, overrideValue);
    } else {
      next[key] = overrideValue;
    }
  }
  return next;
}

function productImage(product) {
  if (product.image_url) return `url(${product.image_url})`;
  return 'linear-gradient(135deg, rgba(113,197,69,0.18), rgba(8,40,54,0.05))';
}

function normalizeSlug(value) {
  const slug = (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const parts = slug.split('-').filter(Boolean);

  if (parts.length >= 2 && parts.length % 2 === 0) {
    const midpoint = parts.length / 2;
    const firstHalf = parts.slice(0, midpoint).join('-');
    const secondHalf = parts.slice(midpoint).join('-');

    if (firstHalf === secondHalf) {
      return firstHalf || 'webshop';
    }
  }

  return slug || 'webshop';
}

function buildDerivedSlug(companyName, cityName) {
  const nameSlug = normalizeSlug(companyName);
  const citySlug = normalizeSlug(cityName);

  if (!citySlug || citySlug === 'webshop') return nameSlug;
  if (nameSlug.includes(citySlug)) return nameSlug;
  if (citySlug.includes(nameSlug)) return citySlug;

  return normalizeSlug(`${companyName || ''} ${cityName || ''}`);
}

function resolveConfiguredSlug(rawSlug, companyName, cityName) {
  const defaultSlug = buildDerivedSlug(companyName, cityName);
  const safeRaw = normalizeSlug(rawSlug);

  if (!safeRaw) return defaultSlug;
  if (safeRaw === 'webshop' && defaultSlug !== 'webshop') return defaultSlug;
  return safeRaw;
}

function formatSlot(slot) {
  if (!Array.isArray(slot) || slot.length < 2) return '';
  return `${slot[0]}-${slot[1]}`;
}

function getOpeningSummary(openingHours) {
  const today = openingHours?.[weekdayKey()];
  if (!today?.enabled || !today?.slots?.length) {
    return {
      title: 'Heute geschlossen',
      detail: 'Der Webshop bleibt sichtbar, Bestellungen sind zurzeit pausiert.',
      tone: 'closed',
    };
  }

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const slots = today.slots
    .filter((slot) => Array.isArray(slot) && slot.length >= 2)
    .map(([start, end]) => {
      const [startHour, startMinute] = start.split(':').map(Number);
      const [endHour, endMinute] = end.split(':').map(Number);
      return {
        start,
        end,
        startMinutes: startHour * 60 + startMinute,
        endMinutes: endHour * 60 + endMinute,
      };
    });

  const activeSlot = slots.find((slot) => currentMinutes >= slot.startMinutes && currentMinutes < slot.endMinutes);
  if (activeSlot) {
    return {
      title: `Jetzt geöffnet bis ${activeSlot.end}`,
      detail: slots.map((slot) => `${slot.start}-${slot.end}`).join(', '),
      tone: 'open',
    };
  }

  const nextSlot = slots.find((slot) => currentMinutes < slot.startMinutes);
  if (nextSlot) {
    return {
      title: `Öffnet heute um ${nextSlot.start}`,
      detail: slots.map((slot) => `${slot.start}-${slot.end}`).join(', '),
      tone: 'soon',
    };
  }

  return {
    title: 'Heute bereits geschlossen',
    detail: slots.map((slot) => `${slot.start}-${slot.end}`).join(', '),
    tone: 'closed',
  };
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
      <path fill="#4285F4" d="M21.6 12.23c0-.68-.06-1.33-.18-1.95H12v3.69h5.38a4.6 4.6 0 0 1-2 3.01v2.5h3.24c1.9-1.75 2.98-4.34 2.98-7.25Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.61-2.44l-3.24-2.5c-.9.6-2.05.95-3.37.95-2.59 0-4.78-1.75-5.56-4.1H3.09v2.58A9.99 9.99 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.44 13.91A5.99 5.99 0 0 1 6.13 12c0-.66.11-1.29.31-1.91V7.51H3.09A9.99 9.99 0 0 0 2 12c0 1.61.39 3.13 1.09 4.49l3.35-2.58Z" />
      <path fill="#EA4335" d="M12 5.98c1.47 0 2.8.5 3.84 1.49l2.88-2.88C16.95 2.94 14.69 2 12 2A9.99 9.99 0 0 0 3.09 7.51l3.35 2.58c.78-2.35 2.97-4.11 5.56-4.11Z" />
    </svg>
  );
}

function OrderTypeCard({ option, onSelect, primaryColor }) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(option.id)}
      className="group flex flex-col items-center justify-center gap-4 rounded-[28px] px-6 py-7 text-center text-white shadow-sm transition-transform hover:-translate-y-0.5"
      style={{ backgroundColor: primaryColor }}
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-white/14 ring-1 ring-white/20 transition-transform group-hover:scale-105">
        <Icon className="h-8 w-8" />
      </div>
      <div>
        <p className="text-xl font-semibold">{option.label}</p>
        <p className="mt-2 text-sm text-white/80">{option.description}</p>
      </div>
    </button>
  );
}

function LegalLinks({ locationPresence }) {
  const legal = locationPresence?.legal || {};
  const links = [
    legal.imprint ? { href: legal.imprint, label: 'Imprint' } : null,
    legal.privacy ? { href: legal.privacy, label: 'Privacy' } : null,
  ].filter(Boolean);

  return (
    <div className="space-y-2 text-center text-sm text-slate-500">
      <p>
        This web shop is powered by <span className="font-semibold text-[#71c545]">FiscalPOS</span>
      </p>
      {links.length ? (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#71c545] underline-offset-4 hover:underline"
            >
              {link.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function WebshopPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutPanelRef = useRef(null);

  const [payload, setPayload] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [flowStage, setFlowStage] = useState('entry');
  const [showAuthPanel, setShowAuthPanel] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [customerUser, setCustomerUser] = useState(null);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [activeMenuId, setActiveMenuId] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState('');
  const [checkout, setCheckout] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    notes: '',
    newsletterOptIn: true,
    fulfillmentType: 'pickup',
    tableName: '',
  });
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  const apiSlug = payload?.api_slug || payload?.slug || slug;
  const presetTableName = searchParams.get('table') || '';
  const presetFulfillmentType = searchParams.get('fulfillment') || '';

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        let data = null;
        try {
          data = await getPublicWebshop(slug);
          const resolvedSlug = normalizeSlug(data?.slug || data?.api_slug || '');
          if (resolvedSlug && resolvedSlug !== normalizeSlug(slug)) {
            navigate(`/webshop/${resolvedSlug}${window.location.search}`, { replace: true });
            return;
          }
        } catch (publicError) {
          const storedUser = localStorage.getItem('fiscalpos_user');
          if (!storedUser) throw publicError;

          const parsedUser = JSON.parse(storedUser);
          const companyId = parsedUser?.company_id || 'default';
          const storedConfigRaw = localStorage.getItem(`fiscalpos_manager_portal_config_${companyId}`);
          const storedConfig = storedConfigRaw ? JSON.parse(storedConfigRaw) : null;
          if (!storedConfig) {
            throw publicError;
          }

          const [settings, categories, products, tables] = await Promise.all([
            getSettings(),
            getCategories(),
            getProducts(),
            getTables(),
          ]);
          const configuredSlug = resolveConfiguredSlug(
            storedConfig?.webshop?.urlSlug || '',
            settings?.company_name || '',
            settings?.company_city || '',
          );

          if (configuredSlug !== normalizeSlug(slug)) {
            navigate(`/webshop/${configuredSlug}${window.location.search}`, { replace: true });
            return;
          }

          data = {
            company_id: companyId,
            slug: configuredSlug,
            api_slug: configuredSlug,
            current_url: `/webshop/${configuredSlug}`,
            company: {
              name: settings?.company_name || 'FiscalPOS',
              address: settings?.company_address || '',
              city: settings?.company_city || '',
              uid: settings?.company_uid || '',
              currency: settings?.currency || 'EUR',
              currency_symbol: settings?.currency_symbol || '€',
            },
            categories,
            products,
            tables,
            default_config: storedConfig,
          };
        }

        if (!mounted) return;

        const stored = data?.company_id ? localStorage.getItem(`fiscalpos_manager_portal_config_${data.company_id}`) : null;
        const storedConfig = stored ? JSON.parse(stored) : null;
        const mergedConfig = data?.config_source === 'database'
          ? (data.default_config || {})
          : mergeDeep(data.default_config || {}, storedConfig || {});

        if (data?.company_id) {
          localStorage.setItem(`fiscalpos_manager_portal_config_${data.company_id}`, JSON.stringify(mergedConfig));
        }

        setPayload(data);
        setPublicConfig(mergedConfig);

        const tableSelectionEnabled = mergedConfig?.webshop?.enableTableSelection || mergedConfig?.orderSettings?.tablesQr?.enableTableSelection;
        const canUsePresetFulfillment = ['pickup', 'delivery', 'dine-in'].includes(presetFulfillmentType);
        const nextFulfillment = canUsePresetFulfillment
          ? presetFulfillmentType
          : tableSelectionEnabled && presetTableName
            ? 'dine-in'
            : mergedConfig?.orderSettings?.orderTypes?.pickup ? 'pickup'
              : mergedConfig?.orderSettings?.orderTypes?.delivery ? 'delivery'
                : mergedConfig?.orderSettings?.orderTypes?.dineIn ? 'dine-in'
                  : 'pickup';
        const salesMenus = getMenusForScope(
          normalizeSalesCatalog(mergedConfig?.salesCatalog || buildEmptySalesCatalog()),
          scopeFromFulfillment(nextFulfillment, 'webshop'),
          'webshop',
        );
        const fallbackMenus = (mergedConfig?.menus?.activeMenus || []).filter((menu) => menu.visible !== false);
        const nextMenu = salesMenus[0]
          || fallbackMenus.find((menu) => menu.type === nextFulfillment)
          || fallbackMenus.find((menu) => menu.type === 'all')
          || fallbackMenus[0]
          || null;

        setActiveMenuId(nextMenu?.id || '');
        setCheckout((previous) => ({
          ...previous,
          newsletterOptIn: mergedConfig?.webshop?.showNewsletterOptIn ?? true,
          fulfillmentType: nextFulfillment,
          tableName: tableSelectionEnabled && presetTableName ? presetTableName : previous.tableName,
        }));
        setFlowStage(canUsePresetFulfillment || (tableSelectionEnabled && presetTableName) ? 'catalog' : 'entry');
      } catch (error) {
        toast.error('Webshop konnte nicht geladen werden');
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [navigate, presetFulfillmentType, presetTableName, slug]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setCustomerUser(user);
      if (user) {
        setCheckout((previous) => ({
          ...previous,
          name: previous.name || user.displayName || '',
          email: previous.email || user.email || '',
        }));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (customerUser) {
      setShowAuthPanel(false);
    }
  }, [customerUser]);

  const currency = payload?.company?.currency || 'EUR';
  const webshopStyle = publicConfig?.webshop || {};
  const allowGuestCheckout = publicConfig?.orderSettings?.general?.allowGuestCheckout ?? true;
  const locationPresence = publicConfig?.locationPresence || {};
  const openingSummary = useMemo(
    () => getOpeningSummary(publicConfig?.orderSettings?.openingHours || {}),
    [publicConfig],
  );

  const salesCatalog = useMemo(
    () => normalizeSalesCatalog(publicConfig?.salesCatalog || buildEmptySalesCatalog()),
    [publicConfig],
  );
  const salesScope = useMemo(
    () => scopeFromFulfillment(checkout.fulfillmentType, 'webshop'),
    [checkout.fulfillmentType],
  );
  const scopedSalesMenus = useMemo(
    () => getMenusForScope(salesCatalog, salesScope, 'webshop'),
    [salesCatalog, salesScope],
  );
  const legacyMenuConfigs = useMemo(
    () => (publicConfig?.menus?.activeMenus || []).filter((menu) => menu.visible !== false),
    [publicConfig],
  );
  const menuConfigs = scopedSalesMenus.length ? scopedSalesMenus : legacyMenuConfigs;
  const activeMenu = menuConfigs.find((menu) => menu.id === activeMenuId) || menuConfigs[0] || null;
  const menuCatalogView = useMemo(
    () => (scopedSalesMenus.length && activeMenu ? buildCatalogViewFromMenu(activeMenu, payload?.products || []) : null),
    [activeMenu, payload, scopedSalesMenus.length],
  );
  const catalogCategories = useMemo(() => {
    if (menuCatalogView) {
      return menuCatalogView.categories;
    }
    const categories = payload?.categories || [];
    if (!activeMenu || !activeMenu.categoryIds?.length) {
      return categories;
    }
    return categories.filter((category) => activeMenu.categoryIds.includes(category.id));
  }, [activeMenu, menuCatalogView, payload]);
  const catalogProducts = useMemo(
    () => menuCatalogView?.products || payload?.products || [],
    [menuCatalogView, payload],
  );
  const catalogProductsById = useMemo(
    () => new Map(catalogProducts.map((product) => [product.id, product])),
    [catalogProducts],
  );
  const categoryLookup = useMemo(
    () => menuCatalogView?.categories || payload?.categories || [],
    [menuCatalogView, payload],
  );
  const categoryLookupById = useMemo(
    () => new Map(categoryLookup.map((category) => [category.id, category])),
    [categoryLookup],
  );
  const featuredProductIds = publicConfig?.menus?.featuredProductIds || [];
  const featuredProducts = featuredProductIds
    .map((productId) => catalogProductsById.get(productId) || (payload?.products || []).find((product) => product.id === productId))
    .filter(Boolean)
    .slice(0, 3);
  const dayConfig = publicConfig?.dailyMenu?.days?.[weekdayKey()] || null;
  const dailyProducts = (dayConfig?.productIds || [])
    .map((productId) => catalogProductsById.get(productId) || (payload?.products || []).find((product) => product.id === productId))
    .filter(Boolean);

  const enabledFulfillmentOptions = useMemo(() => {
    const orderTypes = publicConfig?.orderSettings?.orderTypes || {};
    return [
      orderTypes.pickup ? { id: 'pickup', label: 'Pick up', icon: ShoppingBag, description: 'Ready for collection from your restaurant.' } : null,
      orderTypes.delivery ? { id: 'delivery', label: 'Delivery', icon: Truck, description: 'Let the order come directly to the customer.' } : null,
      orderTypes.dineIn ? { id: 'dine-in', label: 'Dine-in', icon: Store, description: 'QR or table-based ordering inside the venue.' } : null,
    ].filter(Boolean);
  }, [publicConfig]);

  useEffect(() => {
    if (!enabledFulfillmentOptions.length) return;
    if (!enabledFulfillmentOptions.some((option) => option.id === checkout.fulfillmentType)) {
      setCheckout((previous) => ({ ...previous, fulfillmentType: enabledFulfillmentOptions[0].id }));
    }
  }, [checkout.fulfillmentType, enabledFulfillmentOptions]);

  useEffect(() => {
    if (!menuConfigs.length) {
      setActiveMenuId('');
      return;
    }
    if (!menuConfigs.some((menu) => menu.id === activeMenuId)) {
      setActiveMenuId(menuConfigs[0].id);
    }
  }, [activeMenuId, menuConfigs]);

  useEffect(() => {
    if (!catalogCategories.length) {
      setActiveCategoryId('');
      return;
    }
    if (!catalogCategories.some((category) => category.id === activeCategoryId)) {
      setActiveCategoryId(catalogCategories[0].id);
    }
  }, [activeCategoryId, catalogCategories]);

  const visibleProducts = useMemo(() => {
    const allowedCategoryIds = new Set(catalogCategories.map((category) => category.id));
    return catalogProducts.filter((product) => {
      if (allowedCategoryIds.size && !allowedCategoryIds.has(product.category_id)) return false;
      if (activeCategoryId && product.category_id !== activeCategoryId) return false;
      if (search && !`${product.name} ${product.description || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [activeCategoryId, catalogCategories, catalogProducts, search]);

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart],
  );
  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart],
  );

  const syncFirebaseUser = async (user, overrides = {}) => {
    if (!user) return null;

    const name = overrides.name || user.displayName || checkout.name || authForm.name || '';
    const email = overrides.email || user.email || checkout.email || authForm.email || '';
    const phone = overrides.phone ?? checkout.phone;
    const city = overrides.city ?? checkout.city;
    const newsletterOptIn = overrides.newsletterOptIn ?? checkout.newsletterOptIn;

    const customer = await syncPublicWebshopCustomer(apiSlug, {
      name,
      email,
      phone,
      city,
      newsletter_opt_in: newsletterOptIn,
      firebase_uid: user.uid,
      id_token: await user.getIdToken(),
    });

    setCheckout((previous) => ({
      ...previous,
      name: previous.name || name,
      email: previous.email || email,
      phone: previous.phone || phone,
      city: previous.city || city,
    }));

    return customer;
  };

  const syncRedirectUser = useEffectEvent(async (user) => {
    await syncFirebaseUser(user);
    toast.success('Erfolgreich mit Google angemeldet');
  });

  useEffect(() => {
    let mounted = true;

    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(firebaseAuth);
        if (!mounted || !result?.user) return;
        await syncRedirectUser(result.user);
      } catch (error) {
        if (!mounted) return;
        if (error?.code === 'auth/operation-not-allowed') {
          toast.error('Google Login ist im Firebase Projekt noch nicht aktiviert');
          return;
        }
        toast.error(error?.message || 'Google Anmeldung fehlgeschlagen');
      }
    };

    handleRedirectResult();
    return () => {
      mounted = false;
    };
  }, [apiSlug, syncRedirectUser]);

  const focusCheckoutPanel = () => {
    checkoutPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const chooseFulfillment = (optionId, enterCatalog = false) => {
    const nextScope = scopeFromFulfillment(optionId, 'webshop');
    const nextSalesMenus = getMenusForScope(salesCatalog, nextScope, 'webshop');
    const nextLegacyMenus = legacyMenuConfigs.filter(
      (menu) => menu.type === optionId || menu.type === 'all',
    );
    const nextMenu = nextSalesMenus[0]
      || nextLegacyMenus[0]
      || legacyMenuConfigs.find((menu) => menu.type === optionId)
      || legacyMenuConfigs.find((menu) => menu.type === 'all')
      || menuConfigs[0]
      || null;

    setCheckout((previous) => ({
      ...previous,
      fulfillmentType: optionId,
      tableName: optionId === 'dine-in' ? previous.tableName : '',
    }));
    if (nextMenu) {
      setActiveMenuId(nextMenu.id);
    }
    if (enterCatalog) {
      setFlowStage('catalog');
    }
  };

  const addToCart = (product) => {
    if (webshopStyle.readOnlyMode) return;
    const cartKey = product.__menu_key || product.__menu_item_id || product.id;
    setCart((previous) => {
      const existing = previous.find((item) => item.cart_key === cartKey);
      if (existing) {
        return previous.map((item) =>
          item.cart_key === cartKey ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }
      return [
        ...previous,
        {
          cart_key: cartKey,
          product_id: product.id,
          product_name: product.name,
          price: Number(product.price || 0),
          quantity: 1,
          menu_id: product.__menu_id || '',
          menu_item_id: product.__menu_item_id || '',
        },
      ];
    });
  };

  const updateCartItem = (cartKey, nextQuantity) => {
    setCart((previous) => {
      if (nextQuantity <= 0) {
        return previous.filter((item) => item.cart_key !== cartKey);
      }
      return previous.map((item) =>
        item.cart_key === cartKey ? { ...item, quantity: nextQuantity } : item,
      );
    });
  };

  const syncCustomerProfile = async (fallbackName = '') => {
    const name = checkout.name || customerUser?.displayName || fallbackName;
    const email = checkout.email || customerUser?.email || authForm.email;
    if (!name && !email && !checkout.phone) return null;

    return syncPublicWebshopCustomer(apiSlug, {
      name,
      email,
      phone: checkout.phone,
      city: checkout.city,
      newsletter_opt_in: checkout.newsletterOptIn,
      firebase_uid: customerUser?.uid || '',
      id_token: customerUser ? await customerUser.getIdToken() : '',
    });
  };

  const handleGoogleAuth = async () => {
    setIsSubmittingAuth(true);
    try {
      const credential = await signInWithPopup(firebaseAuth, googleAuthProvider);
      await syncFirebaseUser(credential.user);
      toast.success('Erfolgreich mit Google angemeldet');
    } catch (error) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        return;
      }
      if (error?.code === 'auth/popup-blocked' || error?.code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(firebaseAuth, googleAuthProvider);
        return;
      }
      if (error?.code === 'auth/operation-not-allowed') {
        toast.error('Google Login ist im Firebase Projekt noch nicht aktiviert');
        return;
      }
      toast.error(error?.message || 'Google Anmeldung fehlgeschlagen');
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    if (!authForm.email || !authForm.password || (authMode === 'register' && !authForm.name)) {
      toast.error('Bitte alle erforderlichen Felder ausfüllen');
      return;
    }

    setIsSubmittingAuth(true);
    try {
      if (authMode === 'register') {
        const credential = await createUserWithEmailAndPassword(firebaseAuth, authForm.email, authForm.password);
        if (authForm.name) {
          await updateProfile(credential.user, { displayName: authForm.name });
        }
        await syncPublicWebshopCustomer(apiSlug, {
          name: authForm.name,
          email: authForm.email,
          phone: checkout.phone,
          city: checkout.city,
          newsletter_opt_in: checkout.newsletterOptIn,
          firebase_uid: credential.user.uid,
          id_token: await credential.user.getIdToken(),
        });
        setCheckout((previous) => ({
          ...previous,
          name: authForm.name,
          email: authForm.email,
        }));
        toast.success('Kundenkonto erstellt');
      } else {
        const credential = await signInWithEmailAndPassword(firebaseAuth, authForm.email, authForm.password);
        await syncPublicWebshopCustomer(apiSlug, {
          name: credential.user.displayName || checkout.name,
          email: credential.user.email || authForm.email,
          phone: checkout.phone,
          city: checkout.city,
          newsletter_opt_in: checkout.newsletterOptIn,
          firebase_uid: credential.user.uid,
          id_token: await credential.user.getIdToken(),
        });
        setCheckout((previous) => ({
          ...previous,
          name: previous.name || credential.user.displayName || '',
          email: credential.user.email || authForm.email,
        }));
        toast.success('Erfolgreich angemeldet');
      }
      setAuthForm((previous) => ({ ...previous, password: '' }));
      setShowAuthPanel(false);
    } catch (error) {
      toast.error(error?.message || 'Firebase Anmeldung fehlgeschlagen');
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  const handleOrderSubmit = async (event) => {
    event.preventDefault();
    if (!cart.length) {
      toast.error('Bitte zuerst Artikel in den Warenkorb legen');
      return;
    }
    if (!allowGuestCheckout && !customerUser) {
      setShowAuthPanel(true);
      focusCheckoutPanel();
      toast.error('Bitte zuerst mit dem Kundenkonto anmelden');
      return;
    }
    if (!checkout.name || !checkout.email) {
      focusCheckoutPanel();
      toast.error('Name und E-Mail sind für die Bestellung erforderlich');
      return;
    }
    if (
      checkout.fulfillmentType === 'dine-in'
      && (webshopStyle.enableTableSelection || publicConfig?.orderSettings?.tablesQr?.enableTableSelection)
      && !checkout.tableName
    ) {
      focusCheckoutPanel();
      toast.error('Bitte zuerst einen Tisch auswählen');
      return;
    }

    setIsSubmittingOrder(true);
    try {
      await syncCustomerProfile(authForm.name);
      const response = await createPublicWebshopOrder(apiSlug, {
        items: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          notes: item.notes || '',
          menu_id: item.menu_id || '',
          menu_item_id: item.menu_item_id || '',
        })),
        customer: {
          name: checkout.name,
          email: checkout.email,
          phone: checkout.phone,
          city: checkout.city,
          newsletter_opt_in: checkout.newsletterOptIn,
          firebase_uid: customerUser?.uid || '',
          id_token: customerUser ? await customerUser.getIdToken() : '',
        },
        fulfillment_type: checkout.fulfillmentType,
        notes: checkout.notes,
        table_name: checkout.fulfillmentType === 'dine-in' ? checkout.tableName : '',
      });
      setLastOrder(response.summary);
      setCart([]);
      setCheckout((previous) => ({ ...previous, notes: '', tableName: '' }));
      toast.success(`Bestellung ${response.summary.order_number} wurde übernommen`);
      focusCheckoutPanel();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bestellung konnte nicht gesendet werden');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f5f7fb]">
        <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-5 text-sm text-slate-500 shadow-sm">
          Webshop wird geladen…
        </div>
      </div>
    );
  }

  if (!payload || !publicConfig) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f5f7fb]">
        <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
          <CardContent className="space-y-3 p-8 text-center">
            <h1 className="text-3xl font-bold text-[#132d39]">Webshop nicht gefunden</h1>
            <p className="text-slate-500">Für diesen Pfad ist noch kein öffentlicher Shop verfügbar.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#f6f8fc] text-slate-900"
      style={{
        background: `radial-gradient(circle at top left, ${webshopStyle.surfaceColor || '#e8f4eb'} 0%, #f6f8fc 38%)`,
      }}
    >
      {flowStage === 'entry' ? (
        <div className="mx-auto max-w-[860px] px-4 py-6 sm:px-6 lg:py-10">
          <div className="space-y-4 rounded-[36px] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 pb-6 pt-8 text-center sm:px-8">
              <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-[28px] border border-slate-100 bg-[radial-gradient(circle_at_top,_rgba(113,197,69,0.16),_rgba(255,255,255,0.96)_62%)] shadow-sm">
                <span className="text-center text-2xl font-black uppercase tracking-[0.12em] text-[#2b9f5a]">
                  {(payload.company.name || 'Shop').split(' ').slice(0, 2).join(' ')}
                </span>
              </div>
              <h1 className="mt-6 text-4xl font-bold tracking-tight text-[#314b59] sm:text-5xl">{payload.company.name}</h1>
            </div>

            <div className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div>
                <p className="text-sm font-semibold text-[#314b59]">Opening hours</p>
                <p className="mt-1 text-sm text-slate-500">{enabledFulfillmentOptions.map((option) => option.label).join(' • ')}</p>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-600">
                <Clock3 className="h-4 w-4" />
                <span className="font-medium">{openingSummary.title}</span>
              </div>
            </div>
          </div>

          <Card className="mt-4 rounded-[36px] border-slate-200 bg-white shadow-sm">
            <CardContent className="space-y-8 p-6 sm:p-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold tracking-tight text-[#314b59] sm:text-4xl">Order your way!</h2>
                <p className="mt-3 text-base text-slate-500">
                  Wähle zuerst den Bestellweg. Danach öffnen wir das passende Menü und den richtigen Checkout.
                </p>
              </div>

              <div className={`grid gap-4 ${enabledFulfillmentOptions.length > 2 ? 'lg:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {enabledFulfillmentOptions.map((option) => (
                  <OrderTypeCard
                    key={option.id}
                    option={option}
                    onSelect={(optionId) => chooseFulfillment(optionId, true)}
                    primaryColor={webshopStyle.primaryColor || '#2eb35a'}
                  />
                ))}
              </div>

              <div className="grid gap-4 rounded-[30px] border border-slate-200 bg-slate-50 p-5 sm:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Storefront</p>
                  <h3 className="text-2xl font-semibold text-[#132d39]">{payload.company.name}</h3>
                  <div className="space-y-2 text-sm text-slate-500">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {payload.company.address || 'Adresse wird im Manager gepflegt'}
                        {payload.company.city ? `, ${payload.company.city}` : ''}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{openingSummary.detail}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-[26px] bg-white p-5 shadow-sm">
                  <p className="text-sm font-semibold text-[#132d39]">Heute im Fokus</p>
                  {dayConfig?.enabled && dailyProducts.length ? (
                    <div className="mt-3 space-y-2 text-sm text-slate-600">
                      <p className="font-medium text-[#314b59]">{dayConfig.title || publicConfig.dailyMenu.headline || 'Tagesmenü'}</p>
                      {dailyProducts.slice(0, 3).map((product) => (
                        <div key={product.id} className="flex items-center justify-between gap-3">
                          <span>{product.name}</span>
                          <span>{formatMoney(product.price, currency)}</span>
                        </div>
                      ))}
                    </div>
                  ) : featuredProducts.length ? (
                    <div className="mt-3 space-y-2 text-sm text-slate-600">
                      {featuredProducts.map((product) => (
                        <div key={product.id} className="flex items-center justify-between gap-3">
                          <span>{product.name}</span>
                          <span>{formatMoney(product.price, currency)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500">Das passende Menü öffnet sich nach der Auswahl des Bestellwegs.</p>
                  )}
                </div>
              </div>

              <LegalLinks locationPresence={locationPresence} />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="mx-auto max-w-[1580px] px-4 py-4 sm:px-6 lg:px-8">
          <section className="sticky top-4 z-20 rounded-[32px] border border-slate-200 bg-white/90 shadow-sm backdrop-blur">
            <div className="space-y-4 p-4 sm:p-5">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFlowStage('entry')}
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600 transition-colors hover:bg-white"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Webshop</p>
                    <h1 className="mt-1 text-2xl font-bold tracking-tight text-[#132d39] sm:text-3xl">{payload.company.name}</h1>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-600">
                    <Clock3 className="h-4 w-4" />
                    <span className="font-medium">{openingSummary.title}</span>
                  </div>
                  {enabledFulfillmentOptions.map((option) => {
                    const Icon = option.icon;
                    const active = checkout.fulfillmentType === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => chooseFulfillment(option.id)}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                          active ? 'border-transparent text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600'
                        }`}
                        style={active ? { backgroundColor: webshopStyle.primaryColor || '#71c545' } : undefined}
                      >
                        <Icon className="h-4 w-4" />
                        {option.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={focusCheckoutPanel}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 xl:hidden"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    {cartCount} im Warenkorb
                  </button>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.15fr_0.85fr] xl:items-center">
                <div className="flex flex-wrap gap-2">
                  {menuConfigs.map((menu) => (
                    <button
                      key={menu.id}
                      type="button"
                      onClick={() => setActiveMenuId(menu.id)}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        activeMenuId === menu.id ? 'text-white shadow-sm' : 'border border-slate-200 bg-slate-50 text-slate-600'
                      }`}
                      style={activeMenuId === menu.id ? { backgroundColor: webshopStyle.primaryColor || '#71c545' } : undefined}
                    >
                      {menu.name}
                    </button>
                  ))}
                </div>

                <div className="relative w-full">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Artikel suchen"
                    className="h-12 rounded-2xl border-slate-200 pl-10"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {catalogCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategoryId(category.id)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      activeCategoryId === category.id ? 'text-white shadow-sm' : 'border border-slate-200 bg-white text-slate-600'
                    }`}
                    style={activeCategoryId === category.id ? { backgroundColor: webshopStyle.accentColor || '#082836' } : undefined}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <div className="mt-6 grid gap-6 xl:grid-cols-[1.42fr_0.82fr]">
            <div className="space-y-6">
              {(featuredProducts.length > 0 || (dayConfig?.enabled && dailyProducts.length > 0)) ? (
                <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                  <Card className="rounded-[32px] border-slate-200 bg-white shadow-sm">
                    <CardContent className="space-y-4 p-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                        {checkout.fulfillmentType === 'delivery' ? 'Für Delivery beliebt' : checkout.fulfillmentType === 'dine-in' ? 'Im Lokal empfohlen' : 'Für Pick up beliebt'}
                      </p>
                      <div className="grid gap-3 md:grid-cols-3">
                        {(featuredProducts.length ? featuredProducts : visibleProducts.slice(0, 3)).slice(0, 3).map((product) => (
                          <div key={product.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                            <p className="text-sm text-slate-400">
                              {categoryLookupById.get(product.category_id)?.name || 'Artikel'}
                            </p>
                            <p className="mt-2 text-lg font-semibold text-[#132d39]">{product.name}</p>
                            <p className="mt-3 text-sm text-slate-500">{formatMoney(product.price, currency)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {dayConfig?.enabled && dailyProducts.length > 0 ? (
                    <Card className="overflow-hidden rounded-[32px] border-slate-200 shadow-sm">
                      <CardContent
                        className="space-y-4 p-6 text-white"
                        style={{ backgroundColor: webshopStyle.accentColor || '#082836' }}
                      >
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/60">Daily menu</p>
                        <h2 className="text-3xl font-bold tracking-tight">{dayConfig.title || publicConfig.dailyMenu.headline || 'Heute im Fokus'}</h2>
                        <div className="space-y-2 text-sm text-white/80">
                          {dailyProducts.slice(0, 4).map((product) => (
                            <div key={product.id} className="flex items-center justify-between gap-3">
                              <span>{product.name}</span>
                              <span>{formatMoney(product.price, currency)}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                </section>
              ) : null}

              <section className="rounded-[34px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Menu</p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight text-[#132d39]">
                      {activeMenu?.name || 'Aktive Speisekarte'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-500">{visibleProducts.length} Artikel passen aktuell zu deiner Auswahl.</p>
                  </div>
                </div>

                {visibleProducts.length ? (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {visibleProducts.map((product) => (
                      <Card key={product.__menu_key || product.id} className="overflow-hidden rounded-[28px] border-slate-200 shadow-sm">
                        <div className="h-40 bg-cover bg-center" style={{ backgroundImage: productImage(product) }} />
                        <CardContent className="space-y-4 p-5">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                              {categoryLookupById.get(product.category_id)?.name || 'Artikel'}
                            </p>
                            <h3 className="mt-3 text-2xl font-semibold tracking-tight text-[#132d39]">{product.name}</h3>
                            <p className="mt-2 min-h-[48px] text-sm text-slate-500">
                              {product.description || 'Direkt aus deinem Kassa-Artikelstamm im Webshop sichtbar.'}
                            </p>
                          </div>

                          <div className="flex items-end justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Preis</p>
                              <p className="mt-2 text-3xl font-bold tracking-tight text-[#132d39]">{formatMoney(product.price, currency)}</p>
                            </div>
                            <Button
                              onClick={() => addToCart(product)}
                              disabled={webshopStyle.readOnlyMode}
                              className="rounded-2xl px-5 text-white hover:opacity-95"
                              style={{ backgroundColor: webshopStyle.primaryColor || '#71c545' }}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              {webshopStyle.readOnlyMode ? 'Preview' : 'Add'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
                    <h3 className="text-2xl font-semibold text-[#132d39]">Keine Artikel für diese Auswahl</h3>
                    <p className="mt-3 text-sm text-slate-500">Wechsle Menü, Kategorie oder Bestellweg, um weitere Produkte zu sehen.</p>
                  </div>
                )}
              </section>
            </div>

            <div ref={checkoutPanelRef} className="space-y-6 xl:sticky xl:top-28 xl:h-fit">
              <Card className="rounded-[34px] border-slate-200 bg-white shadow-sm">
                <CardContent className="space-y-5 p-5 sm:p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Checkout</p>
                      <h2 className="mt-2 text-3xl font-bold tracking-tight text-[#132d39]">Finalize order</h2>
                    </div>
                    <div className="rounded-full bg-slate-50 px-4 py-2 text-sm font-medium text-slate-600">
                      {cartCount} Artikel
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-[#132d39]">Bestellweg</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {(enabledFulfillmentOptions.find((option) => option.id === checkout.fulfillmentType)?.label) || 'Pickup'}
                        </p>
                      </div>
                      <Button variant="outline" onClick={() => setFlowStage('entry')} className="rounded-2xl border-slate-200">
                        Ändern
                      </Button>
                    </div>
                    <p className="mt-3 text-sm text-slate-500">{openingSummary.detail}</p>
                  </div>

                  {customerUser ? (
                    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#082836] text-white">
                          {customerUser.photoURL ? (
                            <img
                              src={customerUser.photoURL}
                              alt={customerUser.displayName || 'Customer avatar'}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : customerUser.providerData?.some((provider) => provider.providerId === 'google.com') ? (
                            <CircleUserRound className="h-5 w-5" />
                          ) : (
                            <UserRound className="h-5 w-5" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-[#132d39]">{customerUser.displayName || 'Webshop customer'}</p>
                          <p className="truncate text-sm text-slate-500">{customerUser.email}</p>
                        </div>
                        <Button variant="outline" onClick={() => signOut(firebaseAuth)} className="rounded-2xl border-slate-200">
                          <LogOut className="mr-2 h-4 w-4" />
                          Logout
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {allowGuestCheckout && !showAuthPanel ? (
                        <button
                          type="button"
                          onClick={() => setShowAuthPanel(true)}
                          className="w-full rounded-[24px] border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:bg-white"
                        >
                          <p className="text-sm font-semibold text-[#132d39]">Schneller bestellen mit Kundenkonto</p>
                          <p className="mt-2 text-sm text-slate-500">Google oder E-Mail Login ausklappen, damit wir deine Daten und künftige Bestellungen zuordnen können.</p>
                        </button>
                      ) : null}

                      {(showAuthPanel || !allowGuestCheckout) ? (
                        <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                          <div className="mb-4 flex items-start justify-between gap-4">
                            <div>
                              <p className="text-sm font-semibold text-[#132d39]">Customer login</p>
                              <p className="mt-1 text-sm text-slate-500">Google oder E-Mail Login direkt im Checkout.</p>
                            </div>
                            {allowGuestCheckout ? (
                              <button
                                type="button"
                                onClick={() => setShowAuthPanel(false)}
                                className="text-sm font-medium text-slate-500 hover:text-slate-700"
                              >
                                Später
                              </button>
                            ) : null}
                          </div>

                          <form onSubmit={handleAuthSubmit} className="space-y-4">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isSubmittingAuth}
                              onClick={handleGoogleAuth}
                              className="h-12 w-full rounded-2xl border-slate-200 bg-white text-[#132d39] hover:bg-slate-50"
                            >
                              <GoogleMark />
                              <span className="ml-2">{isSubmittingAuth ? 'Bitte warten…' : 'Continue with Google'}</span>
                            </Button>

                            <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                              <div className="h-px flex-1 bg-slate-200" />
                              <span>oder mit E-Mail</span>
                              <div className="h-px flex-1 bg-slate-200" />
                            </div>

                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setAuthMode('login')}
                                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-medium ${authMode === 'login' ? 'text-white' : 'border border-slate-200 text-slate-600'}`}
                                style={authMode === 'login' ? { backgroundColor: webshopStyle.accentColor || '#082836' } : undefined}
                              >
                                Login
                              </button>
                              <button
                                type="button"
                                onClick={() => setAuthMode('register')}
                                className={`flex-1 rounded-2xl px-4 py-3 text-sm font-medium ${authMode === 'register' ? 'text-white' : 'border border-slate-200 text-slate-600'}`}
                                style={authMode === 'register' ? { backgroundColor: webshopStyle.primaryColor || '#71c545' } : undefined}
                              >
                                Register
                              </button>
                            </div>

                            {authMode === 'register' ? (
                              <Input
                                value={authForm.name}
                                onChange={(event) => setAuthForm((previous) => ({ ...previous, name: event.target.value }))}
                                placeholder="Vollständiger Name"
                                autoComplete="name"
                                className="h-12 rounded-2xl border-slate-200 bg-white"
                              />
                            ) : null}
                            <Input
                              value={authForm.email}
                              onChange={(event) => setAuthForm((previous) => ({ ...previous, email: event.target.value }))}
                              placeholder="E-Mail"
                              type="email"
                              autoComplete="email"
                              className="h-12 rounded-2xl border-slate-200 bg-white"
                            />
                            <Input
                              value={authForm.password}
                              onChange={(event) => setAuthForm((previous) => ({ ...previous, password: event.target.value }))}
                              placeholder="Passwort"
                              type="password"
                              autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                              className="h-12 rounded-2xl border-slate-200 bg-white"
                            />
                            {!allowGuestCheckout ? (
                              <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                Für diesen Webshop ist ein Kundenlogin vor dem Checkout erforderlich.
                              </div>
                            ) : null}
                            <Button
                              type="submit"
                              disabled={isSubmittingAuth}
                              className="h-12 w-full rounded-2xl text-white hover:opacity-95"
                              style={{ backgroundColor: authMode === 'register' ? webshopStyle.primaryColor || '#71c545' : webshopStyle.accentColor || '#082836' }}
                            >
                              <LogIn className="mr-2 h-4 w-4" />
                              {isSubmittingAuth ? 'Bitte warten…' : authMode === 'register' ? 'Create account' : 'Login'}
                            </Button>
                          </form>
                        </div>
                      ) : null}
                    </>
                  )}

                  <div className="space-y-3 rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                    {cart.length ? cart.map((item) => (
                      <div key={item.cart_key || item.product_id} className="rounded-[20px] bg-white p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-[#132d39]">{item.product_name}</p>
                            <p className="mt-1 text-sm text-slate-500">{formatMoney(item.price, currency)} pro Stück</p>
                          </div>
                          <p className="font-semibold text-[#132d39]">{formatMoney(item.price * item.quantity, currency)}</p>
                        </div>
                        <div className="mt-4 flex items-center justify-between">
                          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2 py-1">
                            <button type="button" onClick={() => updateCartItem(item.cart_key || item.product_id, item.quantity - 1)} className="rounded-full p-1 text-slate-500 hover:bg-white">
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="min-w-[28px] text-center text-sm font-semibold text-[#132d39]">{item.quantity}</span>
                            <button type="button" onClick={() => updateCartItem(item.cart_key || item.product_id, item.quantity + 1)} className="rounded-full p-1 text-slate-500 hover:bg-white">
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          <button type="button" onClick={() => updateCartItem(item.cart_key || item.product_id, 0)} className="text-sm font-medium text-rose-600">
                            Entfernen
                          </button>
                        </div>
                      </div>
                    )) : (
                      <div className="rounded-[20px] border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
                        Noch keine Artikel ausgewählt.
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleOrderSubmit} className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                      <Input
                        value={checkout.name}
                        onChange={(event) => setCheckout((previous) => ({ ...previous, name: event.target.value }))}
                        placeholder="Name"
                        autoComplete="name"
                        className="h-12 rounded-2xl border-slate-200"
                      />
                      <Input
                        value={checkout.email}
                        onChange={(event) => setCheckout((previous) => ({ ...previous, email: event.target.value }))}
                        placeholder="E-Mail"
                        type="email"
                        autoComplete="email"
                        className="h-12 rounded-2xl border-slate-200"
                      />
                      <Input
                        value={checkout.phone}
                        onChange={(event) => setCheckout((previous) => ({ ...previous, phone: event.target.value }))}
                        placeholder="Telefon"
                        autoComplete="tel"
                        className="h-12 rounded-2xl border-slate-200"
                      />
                      <Input
                        value={checkout.city}
                        onChange={(event) => setCheckout((previous) => ({ ...previous, city: event.target.value }))}
                        placeholder="Ort"
                        autoComplete="address-level2"
                        className="h-12 rounded-2xl border-slate-200"
                      />
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                      <select
                        value={checkout.fulfillmentType}
                        onChange={(event) => chooseFulfillment(event.target.value)}
                        className="h-12 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
                      >
                        {enabledFulfillmentOptions.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                      {checkout.fulfillmentType === 'dine-in' && (webshopStyle.enableTableSelection || publicConfig?.orderSettings?.tablesQr?.enableTableSelection) ? (
                        <select
                          value={checkout.tableName}
                          onChange={(event) => setCheckout((previous) => ({ ...previous, tableName: event.target.value }))}
                          className="h-12 rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
                        >
                          <option value="">Tisch wählen</option>
                          {(payload.tables || []).map((table) => (
                            <option key={table.id} value={table.name}>{table.name}</option>
                          ))}
                        </select>
                      ) : null}
                    </div>

                    {webshopStyle.showNewsletterOptIn ? (
                      <label className="flex items-start gap-3 rounded-[20px] border border-slate-200 p-4 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={checkout.newsletterOptIn}
                          onChange={(event) => setCheckout((previous) => ({ ...previous, newsletterOptIn: event.target.checked }))}
                          className="mt-1 h-4 w-4 rounded border-slate-300"
                        />
                        <span>Ich möchte Aktionen und Neuigkeiten von {payload.company.name} erhalten.</span>
                      </label>
                    ) : null}

                    <Textarea
                      value={checkout.notes}
                      onChange={(event) => setCheckout((previous) => ({ ...previous, notes: event.target.value }))}
                      placeholder="Hinweise zur Bestellung"
                      className="min-h-[110px] rounded-[24px] border-slate-200"
                    />

                    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>Artikel im Warenkorb</span>
                        <span>{cartCount}</span>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-lg font-semibold text-[#132d39]">Gesamt</span>
                        <span className="text-2xl font-bold text-[#132d39]">{formatMoney(cartTotal, currency)}</span>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmittingOrder || webshopStyle.readOnlyMode}
                      className="h-12 w-full rounded-2xl text-white hover:opacity-95"
                      style={{ backgroundColor: webshopStyle.primaryColor || '#71c545' }}
                    >
                      <ChevronRight className="mr-2 h-4 w-4" />
                      {webshopStyle.readOnlyMode ? 'Read-only preview' : isSubmittingOrder ? 'Bestellung wird gesendet…' : 'Bestellung an Kassa senden'}
                    </Button>

                    {webshopStyle.readOnlyMode ? (
                      <div className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                        Preview mode ist aktiv. Menü und Design bleiben sichtbar, Checkout und Order Push sind gesperrt.
                      </div>
                    ) : null}
                  </form>

                  {lastOrder ? (
                    <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                      <p className="font-semibold">Bestellung übernommen</p>
                      <p className="mt-1">Nummer: {lastOrder.order_number}</p>
                      <p className="mt-1">Summe: {formatMoney(lastOrder.total, currency)}</p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </div>

          {cart.length ? (
            <div className="fixed inset-x-0 bottom-4 z-30 px-4 xl:hidden">
              <button
                type="button"
                onClick={focusCheckoutPanel}
                className="flex w-full items-center justify-between rounded-[24px] border border-slate-200 bg-[#132d39] px-5 py-4 text-left text-white shadow-lg"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Warenkorb</p>
                  <p className="mt-1 text-lg font-semibold">{cartCount} Artikel</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/60">Gesamt</p>
                  <p className="mt-1 text-2xl font-bold">{formatMoney(cartTotal, currency)}</p>
                </div>
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
